import { createContext } from "react";
import type { Role } from "@/shared/types/role";

/** A tenant the current user can act within (from GET /api/auth/me). */
export interface TenantRef {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly role: Role;
}

export interface SessionUser {
  readonly userId: string;
  readonly email: string;
  readonly superadmin: boolean;
  /** Effective role in the ACTIVE tenant. */
  readonly role: Role;
  /** Active tenant id, or null for a superadmin with no tenant context. */
  readonly activeTenantId: string | null;
  /** Every tenant this user can switch between. */
  readonly tenants: readonly TenantRef[];
}

/**
 * Why a Firebase-authenticated user can still be denied entry. A valid Firebase
 * token is necessary but NOT sufficient: the user must also resolve to an active
 * Growth account with a tenant membership (ADR-0016). `null` = no rejection.
 *
 * - `not-provisioned`: the API rejected the identity (401/403/404 from
 *   /api/auth/me). No usable Growth account — sign out, show "contact support".
 * - `verification-failed`: the API was unreachable (network / 5xx). Deny access
 *   but keep the Firebase session so a retry can re-verify without re-login.
 */
export type AuthRejection = "not-provisioned" | "verification-failed";

export interface AuthContextValue {
  readonly status: "loading" | "authenticated" | "unauthenticated";
  readonly user: SessionUser | null;
  readonly rejection: AuthRejection | null;
  /** Switch active tenant, persist it, and re-verify the session for it. */
  readonly switchTenant: (tenantId: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
